/* eslint-disable react/no-unescaped-entities */
import PillarPage from "./PillarPage";

const HERO = "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1800&q=85&auto=format&fit=crop";

const TOC = [
  { id: "headline", label: "The headline — is Dubai property really tax-free?" },
  { id: "purchase-fees", label: "One-off fees you do pay at purchase" },
  { id: "annual-costs", label: "Annual property costs (not taxes)" },
  { id: "rental-income", label: "Tax on rental income" },
  { id: "capital-gains", label: "Tax on capital gains from resale" },
  { id: "corporate-tax", label: "When corporate tax applies to property" },
  { id: "vat", label: "VAT and Dubai real estate" },
  { id: "inheritance", label: "Inheritance tax & DIFC wills" },
  { id: "home-country", label: "Home-country tax on your Dubai property" },
  { id: "residency-tax", label: "How residency changes the picture" },
];

const SECTIONS = [
  {
    id: "headline",
    heading: "Is Dubai property really tax-free?",
    body: (
      <>
        <p>
          Almost. In 2026, <b>the UAE levies no personal income tax, no capital gains tax and no
          annual property tax on residential real estate</b>. There is no municipal property tax
          equivalent to UK council tax, US state property tax or India's property tax. What you do
          pay is a one-off 4% Dubai Land Department transfer fee at the point of purchase, plus
          annual service charges (which are strata-style, not a tax), and — if you rent the property
          out — a small municipality-fee levy embedded in the tenant's DEWA utility bill.
        </p>
        <p>
          This makes Dubai one of the most tax-efficient residential property markets globally. For
          most international owners the effective annual tax burden on a paid-off Dubai investment
          is <b>zero</b>. The catch — and it is a real catch — is that your <em>home country</em> may
          still tax the property. That is covered in section 9 of this guide.
        </p>
      </>
    ),
  },
  {
    id: "purchase-fees",
    heading: "One-off fees you do pay at purchase",
    body: (
      <>
        <p>
          Budget approximately <b>7% on top of the sticker price</b> for the closing round-trip.
          Here is the exact breakdown:
        </p>
        <table>
          <thead>
            <tr>
              <th>Fee</th>
              <th>Rate</th>
              <th>On AED 3M</th>
              <th>Paid to</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>DLD transfer fee</td><td>4.0%</td><td>AED 120,000</td><td>Dubai Land Department</td></tr>
            <tr><td>Agent commission</td><td>2.0% + 5% VAT</td><td>AED 63,000</td><td>Point Penta</td></tr>
            <tr><td>Trustee / registration</td><td>~AED 4,000 (flat)</td><td>AED 4,000</td><td>DLD-appointed trustee</td></tr>
            <tr><td>Title deed issuance</td><td>~AED 580 (flat)</td><td>AED 580</td><td>Dubai Land Department</td></tr>
            <tr><td>NOC fee (from developer)</td><td>AED 500 – 5,000</td><td>~AED 1,500</td><td>Developer</td></tr>
            <tr><td>Mortgage registration (if any)</td><td>0.25% of loan</td><td>—</td><td>Dubai Land Department</td></tr>
          </tbody>
        </table>
        <p>
          The DLD 4% fee is <em>not</em> a tax in the strict sense — it is a registration and land
          administration charge. It is one-off, non-recurring and stamped into the transaction the
          day the title deed is issued.
        </p>
      </>
    ),
  },
  {
    id: "annual-costs",
    heading: "Annual property costs — these are not taxes, but budget for them",
    body: (
      <>
        <ul>
          <li><b>Service charges</b> — AED 8 to AED 45 per square foot per annum depending on the community. JVC ~AED 12/sqft; Downtown ~AED 22/sqft; Palm Jumeirah ~AED 35–45/sqft. Levied by the master developer's owners association (OA), not a government tax.</li>
          <li><b>Chiller / district cooling</b> — often billed separately by Empower or Emicool at ~AED 0.65–0.90 per RT (Refrigeration Ton) plus a fixed connection fee.</li>
          <li><b>DEWA utilities</b> — Dubai Electricity and Water Authority. Metered consumption plus a 5% municipality fee (this is the closest thing to a resident-facing property tax — see section 4).</li>
          <li><b>Home insurance</b> — optional but recommended, ~AED 500–2,000 per year for a mid-market apartment.</li>
          <li><b>Property management fee</b> — 5–8% of gross rent if you use a manager (recommended for absentee owners).</li>
        </ul>
      </>
    ),
  },
  {
    id: "rental-income",
    heading: "Tax on rental income — what actually gets deducted",
    body: (
      <>
        <p>
          There is no federal income tax on rental income in the UAE. However there are two small
          deductions embedded in the ecosystem:
        </p>
        <ul>
          <li><b>Municipality fee (5% of annual rent)</b> — technically levied on the tenant and appears on their DEWA bill. Landlords are not billed directly but it does slightly compress what tenants are willing to pay.</li>
          <li><b>Ejari registration fee</b> — AED 220 per lease per year, usually paid by the tenant.</li>
        </ul>
        <p>
          For a typical AED 3M apartment renting at AED 180,000/year with AED 12,000 service
          charges, the <b>net cash yield after all fees is roughly 5.4%</b>, with zero income tax
          leakage to any UAE authority. Repatriating the rent to your home country is fully
          unrestricted — the UAE has no exchange controls and no withholding on outbound wires.
        </p>
      </>
    ),
  },
  {
    id: "capital-gains",
    heading: "Tax on capital gains from resale",
    body: (
      <>
        <p>
          <b>Zero.</b> The UAE does not tax capital gains for individuals — this applies whether you
          hold the property for 30 days or 30 years. If you buy an apartment for AED 2M and sell for
          AED 3M, the AED 1M gain is entirely yours (subject to your <em>home country</em>'s CGT
          rules — see section 9).
        </p>
        <p>
          At resale you pay the same 4% DLD transfer fee (usually split 50/50 with the buyer by
          convention, but by law payable by the transferee) and the agency fee. There is no
          progressive tax scale, no holding-period differentiation, and no stamp duty comparable to
          the UK or Singapore models.
        </p>
      </>
    ),
  },
  {
    id: "corporate-tax",
    heading: "When corporate tax applies to property",
    body: (
      <>
        <p>
          Since June 2023 the UAE has levied <b>9% federal corporate tax on business profits above
          AED 375,000</b>. For residential property investors this rarely bites, because rental
          income earned by individuals in their personal capacity is expressly outside the corporate
          tax net. The 9% only applies if:
        </p>
        <ul>
          <li>The property is held through a <b>UAE mainland or free-zone company</b>, and</li>
          <li>The rental / trading activity constitutes a "business" (multiple properties, active management, commercial premises), and</li>
          <li>Net taxable profit exceeds AED 375,000 in the tax year.</li>
        </ul>
        <p>
          Point Penta's investment desk models this in your yield sheet at the outset. For 90% of
          international buyers holding one or two Dubai apartments in their personal name, the
          corporate tax question does not arise.
        </p>
      </>
    ),
  },
  {
    id: "vat",
    heading: "VAT and Dubai real estate",
    body: (
      <>
        <p>UAE VAT is 5% but the residential real estate treatment is favourable:</p>
        <ul>
          <li><b>Sale of new residential property (first 3 years from completion)</b> — <em>zero-rated</em>. Developer charges 0% VAT.</li>
          <li><b>Sale of second-hand residential property</b> — <em>exempt</em>. No VAT.</li>
          <li><b>Residential rent</b> — <em>exempt</em>. Landlords do not charge VAT to tenants.</li>
          <li><b>Agent commission on residential deals</b> — 5% VAT applies (i.e. 2% commission + 5% VAT on top).</li>
          <li><b>Commercial property (sale and lease)</b> — 5% VAT applies to both.</li>
          <li><b>Serviced apartments / short-term lets</b> — 5% VAT applies.</li>
        </ul>
      </>
    ),
  },
  {
    id: "inheritance",
    heading: "Inheritance tax & why every Dubai property owner needs a DIFC will",
    body: (
      <>
        <p>
          <b>The UAE levies no inheritance tax</b>. However, in the absence of a will registered
          under a common-law framework, Dubai property owned by non-Muslims is by default subject to
          Sharia inheritance principles under UAE Personal Status Law — which can allocate shares
          very differently from your intentions (spouses typically inherit 1/8 rather than the whole
          estate; sons take twice the share of daughters; step-children take nothing).
        </p>
        <p>
          The definitive solution — and one Point Penta walks every buyer through at handover — is
          a <b>DIFC Wills Service Centre will</b>. Registered in the Dubai International Financial
          Centre under English common-law principles, a DIFC will overrides Sharia default rules
          and gives you full testamentary freedom over your UAE assets. Cost: AED 10,000 flat for a
          single will, valid indefinitely.
        </p>
      </>
    ),
  },
  {
    id: "home-country",
    heading: "Home-country tax on your Dubai property — the hidden variable",
    body: (
      <>
        <p>
          UAE tax freedom does not extend beyond UAE borders. If you are tax-resident in a country
          that taxes global rental income and capital gains, that country will most likely tax your
          Dubai property too. Every major jurisdiction operates a different rule set — a summary:
        </p>
        <ul>
          <li><b>United Kingdom</b> — HMRC taxes UK-resident owners on Dubai rent (self-assessment, at marginal rate up to 45%) and CGT on disposal (18/24%). Non-UK-domiciled remittance basis has changed materially from April 2025 — get UK-specific advice.</li>
          <li><b>United States</b> — global taxation regardless of residency. IRS taxes rent, capital gains and requires FBAR / FATCA reporting on UAE bank accounts holding proceeds. UAE-US tax treaty is limited.</li>
          <li><b>India</b> — resident Indians are taxed on Dubai rental income at marginal slabs and CGT at 20% (with indexation). DTAA credit is available for the 5% UAE municipality fee (nominal).</li>
          <li><b>Canada / Australia</b> — global taxation, similar to UK model.</li>
          <li><b>France / Germany / Netherlands</b> — global taxation, DTAA-driven, wealth-tax exposure in France above certain thresholds.</li>
          <li><b>Russia, KSA, Kuwait, Qatar, Bahrain</b> — largely nil incremental exposure.</li>
        </ul>
        <p>
          Point Penta does not provide home-country tax advice — we always recommend a
          jurisdiction-specific advisor. What we do provide is the complete documentation package
          your home-country accountant will need: notarised title deed, escrow receipts, Ejari, DLD
          valuation certificate, annual service charge statements, and (where relevant) proof of
          UAE residency to claim tie-breaker treatment under a double-tax treaty.
        </p>
      </>
    ),
  },
  {
    id: "residency-tax",
    heading: "How UAE residency changes the tax picture",
    body: (
      <>
        <p>
          The single most powerful tax move for many international owners is not the property itself
          — it is the residency that comes with it. UAE tax residency (183+ days physical presence,
          or the 90-day/UAE-centre-of-vital-interests rule under Ministerial Decision No. 27 of 2023)
          means:
        </p>
        <ul>
          <li>You obtain a UAE Tax Residency Certificate from the FTA, which is recognised under most bilateral DTAAs.</li>
          <li>Your home country will typically <em>stop</em> taxing your worldwide income once you break residency there (subject to local rules — the US is the notable exception).</li>
          <li>Your Dubai rental income and capital gains stay 0%-taxed.</li>
          <li>Golden Visa holders can hold UAE tax residency with no minimum stay requirement — a unique combination globally.</li>
        </ul>
        <p>
          For a UK-resident buying a AED 3M Dubai apartment, breaking UK tax residency and moving
          to Dubai typically improves the net-after-tax yield from ~3.1% to ~5.4% — before any
          consideration of capital appreciation or corporate structure. This is the underlying case
          behind Dubai's magnetism for UK, Indian and European private wealth in 2024–2026.
        </p>
      </>
    ),
  },
];

const FAQS = [
  { q: "Is there any property tax in Dubai?", a: "No. There is no annual property tax, no council tax equivalent and no wealth tax in Dubai. You pay a one-off 4% DLD transfer fee at purchase, plus ongoing service charges (levied by the community's owners association, not the government). No recurring government levy on the property itself." },
  { q: "Do I pay tax on rental income from a Dubai property?", a: "No UAE federal income tax on residential rental income. A 5% municipality fee is embedded in the tenant's DEWA utility bill (not paid by the landlord directly). Landlords repatriate rent freely — no exchange controls, no withholding." },
  { q: "Is there capital gains tax when I sell a Dubai property?", a: "Zero UAE capital gains tax for individuals, regardless of holding period. The full gain is yours. You will pay the 4% DLD transfer fee at resale (usually shared with the buyer by convention). Your home country may still levy CGT on the gain — that is the variable to model." },
  { q: "Does the 9% corporate tax apply to Dubai property investors?", a: "Only if the property is held through a UAE company AND the activity qualifies as a business (multiple units, active management, commercial focus) AND profits exceed AED 375,000. Individual investors holding 1–2 apartments in personal name are outside the corporate tax net." },
  { q: "Do I charge VAT when I rent out my Dubai apartment?", a: "No. Residential rent is VAT-exempt in the UAE. You do pay 5% VAT on your agent's commission and on service charges billed by the owners association. Commercial property is subject to 5% VAT on both sale and rent." },
  { q: "Will my home country still tax my Dubai property?", a: "Almost certainly yes if you are tax-resident there. UK, US, India, Canada, Australia, France, Germany and most OECD jurisdictions tax worldwide rental income and capital gains for their residents. The single most effective response is often to break home-country tax residency and become UAE tax-resident — which the Golden Visa enables with no minimum stay requirement." },
  { q: "Is there inheritance tax on Dubai property?", a: "No UAE inheritance tax. However, without a DIFC-registered will, non-Muslim inheritance defaults to Sharia principles which may allocate shares very differently from your intent. Every Point Penta client is advised to register a DIFC will (~AED 10,000 flat, valid indefinitely) at time of purchase." },
  { q: "Are closing costs on a Dubai property tax-deductible?", a: "Not against UAE income (there is no UAE income tax to deduct them against). They are typically capitalised into your cost base for home-country capital gains purposes — so keep every receipt (DLD, agency VAT invoice, mortgage registration) for eventual disposal. Point Penta issues consolidated closing statements that home-country accountants accept." },
];

export default function PropertyTaxPillar() {
  return (
    <PillarPage
      slug="dubai-property-tax-guide-2026"
      category="Tax & structuring"
      title="Dubai Property Tax — 2026 Complete Owner's Guide"
      hero={HERO}
      updated="Feb 2026"
      intro="Dubai has no annual property tax, no rental income tax, no capital gains tax and no inheritance tax. This guide details every fee that does apply, when the 9% corporate tax bites, how VAT works on residential vs commercial, and — critically — how your home country will still tax your Dubai property unless you change residency."
      toc={TOC}
      sections={SECTIONS}
      faqs={FAQS}
    />
  );
}
